/**
 * ROULETTE, SHARED MODE — the lobby (Phase E, plan §5.7, 2026-09-06).
 *
 * §5.7: "Lobby lists `shared_tables` (occupancy = real seats + fake seats)" and "`tables.ts`
 * survives only for lobby texture". This module is the bridge between the two: it reads the
 * real rooms from `GET /roulette/tables`, dresses each one in the texture the local lobby
 * invented (a readable room code, a seat count, a pace badge) and hands `Lobby.tsx` the same
 * `Table` shape it already renders — so the lobby screen the owner signed off does not change
 * at all, it just stops being fiction.
 *
 * ── WHAT IS REAL HERE AND WHAT IS TEXTURE ────────────────────────────────────────────────
 *
 *   REAL      the list of rooms (`shared_tables`), each room's PACE and window, the live
 *             countdown to its lock, the number of people who have actually bet on the round
 *             in play, and the last results the table really produced.
 *   TEXTURE   the room code (`7KQ4-2M`), and the fake half of the seat count. Both are pure
 *             functions of the SERVER's `fake_seed` and the round number — never of the device
 *             seed the local lobby used — so two phones looking at Table 2 read the same code
 *             and the same crowd, which is the whole point of a shared table (plan §5.4).
 *
 * The owner's brief keeps the mock crowd (O3: "keep the mock users + their betting theatre"),
 * so occupancy is real bettors PLUS fake seats and can never read fewer people than are
 * genuinely sitting there.
 *
 * ── WHY THE LAST-RESULTS STRIP COSTS AN EXTRA CALL ───────────────────────────────────────
 * `listTables` returns each table's LIVE round and nothing else — there is no history in it,
 * and adding one to the shared service for roulette's benefit is out of bounds. So the strip
 * is seeded per table from `GET /state?table=`, once on entering the lobby, and refreshed for
 * a table only when its round number has actually advanced past the one we seeded from (and at
 * most every 20 s). With the three seeded roulette tables that is three calls on entry and
 * roughly one every twenty seconds after — against a `/state` budget of 300/min.
 *
 * It is worth the calls. The alternative is `recentResults()`, and a derived number strip on a
 * REAL table is the one thing §5.7 explicitly deletes: it would contradict the table's own
 * record the moment two players compared screens.
 */

import { api } from '@baaligames/shared-ui/lib/api';
import { occupancyOf, seatBaseFor, shortIdFor } from './sharedTiming';
import { type Table } from './tables';

/** One row of `GET /roulette/tables`. */
type SrvLobbyTable = {
  id: number; name: string; pace: string;
  windowMs: number; intermissionMs: number; fakeSeed: number; sortOrder: number;
  round: {
    id: number; no: number; status: string;
    opensAt: number | null; locksAt: number | null;
    resolvesAt: number | null; settlesAt: number | null; nextOpensAt: number | null;
    pot?: number; bettors?: number;
  } | null;
};

/** What `Lobby.tsx` needs on top of a `Table` when the numbers are real. */
export type LiveTableInfo = {
  /** seconds until this table stops taking chips */
  left: number;
  /** the table's real last results, newest first (empty until it has settled one) */
  hist: number[];
  /** real bettors + fake seats */
  seated: number;
};

export type SharedLobby = {
  /** the rooms, in the server's own sort order, in the shape `Lobby.tsx` already draws */
  tables: Table[];
  /** per-table live numbers, keyed by `Table.id` */
  live: Record<string, LiveTableInfo>;
  /** `Table.id` → `shared_tables.id`, which is what every API call actually needs */
  serverIdOf: Record<string, number>;
  /** true once the first `/tables` has landed */
  ready: boolean;
  error: string | null;
};

export const EMPTY_LOBBY: SharedLobby = { tables: [], live: {}, serverIdOf: {}, ready: false, error: null };

/** Map one server row into the `Table` the lobby renders. Pure. */
export function toLobbyTable(row: SrvLobbyTable, minBet: number): Table {
  const seed = Number(row.fakeSeed) >>> 0;
  return {
    seed,
    id: shortIdFor(Number(row.id), seed),
    name: String(row.name || `Table ${row.id}`),
    // `phase` and `seatBase` were the local virtual clock's inputs. The clock is the server's
    // now, so `phase` is inert here and only `seatBase` is still read (by `occupancyOf`).
    phase: 0,
    minBet,
    fast: String(row.pace) === 'quick',
    seatBase: seatBaseFor(seed),
  };
}

/**
 * The lobby, polled.
 *
 * Returns a stop function. `onChange` fires whenever anything the screen shows has moved, and
 * the caller re-reads via the getter it was handed — the same subscribe/read shape the table
 * clock uses, so the lobby and the table behave alike.
 */
export function startSharedLobby(onChange: (l: SharedLobby) => void): () => void {
  let dead = false;
  let minBet = 10;
  let cur: SharedLobby = { ...EMPTY_LOBBY };
  const histBy = new Map<number, { at: number; roundNo: number; hist: number[] }>();
  let timer = 0;

  const emit = () => { if (!dead) onChange(cur); };

  /** The last results for one server table, seeded once and refreshed only when it moves on. */
  async function seedHistory(serverId: number, roundNo: number) {
    const have = histBy.get(serverId);
    const now = Date.now();
    if (have && have.roundNo >= roundNo && now - have.at < 20_000) return;
    if (have && now - have.at < 20_000) return;
    try {
      const st = await api.get<{ history: { result: { n: number } | null }[] }>(`/roulette/state?table=${serverId}`);
      if (dead) return;
      const hist: number[] = [];
      for (const h of st.history || []) {
        const n = Number(h?.result?.n);
        if (Number.isInteger(n) && n >= 0 && n <= 36) hist.push(n);
      }
      histBy.set(serverId, { at: Date.now(), roundNo, hist });
    } catch {
      // A table whose history we cannot read shows no strip rather than a made-up one.
      histBy.set(serverId, { at: Date.now(), roundNo, hist: have?.hist ?? [] });
    }
  }

  async function poll() {
    try {
      const j = await api.get<{ tables: SrvLobbyTable[] }>('/roulette/tables');
      if (dead) return;
      const rows = Array.isArray(j?.tables) ? j.tables : [];
      const tables: Table[] = [];
      const live: Record<string, LiveTableInfo> = {};
      const serverIdOf: Record<string, number> = {};
      const nowSrv = Date.now();
      for (const row of rows) {
        const t = toLobbyTable(row, minBet);
        tables.push(t);
        serverIdOf[t.id] = Number(row.id);
        const r = row.round;
        const roundNo = Number(r?.no ?? 0);
        // The countdown a player is choosing between rooms on is "how long until this table
        // stops taking chips", which is the lock — not the settle and not the next open.
        const locks = r?.locksAt == null ? null : Number(r.locksAt);
        const left = locks == null ? 0 : Math.max(0, Math.ceil((locks - nowSrv) / 1000));
        live[t.id] = {
          left,
          hist: (histBy.get(Number(row.id))?.hist ?? []),
          seated: occupancyOf(Number(row.fakeSeed) >>> 0, roundNo, t.seatBase, Number(r?.bettors ?? 0)),
        };
        void seedHistory(Number(row.id), roundNo);
      }
      cur = { tables, live, serverIdOf, ready: true, error: null };
      emit();
    } catch (e) {
      if (dead) return;
      cur = { ...cur, ready: cur.ready, error: e instanceof Error ? e.message : 'Could not reach the tables.' };
      emit();
    }
  }

  // The bet floor is a config read, not a per-table one: `shared_tables` carries no minimum, so
  // the house floor is what every room advertises. One call, and the lobby draws either way.
  void api.get<{ bet: { min: number } }>('/roulette/config')
    .then((c) => { minBet = Number(c?.bet?.min) || 10; })
    .catch(() => { /* the default floor stands */ })
    .finally(() => { void poll(); });

  // 2 s: fast enough that a countdown does not visibly stutter, slow enough that nine cards on
  // one screen cost 30 reads a minute against a 300-a-minute budget.
  timer = window.setInterval(() => { void poll(); }, 2_000);

  return () => {
    dead = true;
    window.clearInterval(timer);
  };
}
